import { useMemo, useRef } from "react";
import { Star, ChevronUp, ChevronDown } from "lucide-react";
import { fmtEUR, stockBadge } from "@/lib/api";

/**
 * Shared product catalogue panel used by both POSRetail and Restaurant.
 *
 * Renders three stacked regions:
 *   1. Category strip (configurable cols/rows from settings.categories_cols/rows)
 *   2. Product grid    (configurable cols/rows from settings.products_cols/rows,
 *                       with auto-scaling tile padding + font size)
 *   3. Pinned favourites row + scroll arrows
 *
 * All "hidden" / "paused" rules and favourites selection live here so the two
 * modules can never drift apart.
 */
function readableTextColor(hex) {
  if (!hex) return "#0f172a";
  const m = hex.replace("#", "");
  const v =
    m.length === 3
      ? m.split("").map((c) => parseInt(c + c, 16))
      : [parseInt(m.slice(0, 2), 16), parseInt(m.slice(2, 4), 16), parseInt(m.slice(4, 6), 16)];
  const L = (0.299 * v[0] + 0.587 * v[1] + 0.114 * v[2]) / 255;
  return L > 0.62 ? "#0f172a" : "#ffffff";
}

export default function ProductCatalogPanel({
  products,
  categories,
  activeCat,
  setActiveCat,
  settings,
  onProductClick,
  testidPrefix = "pos",
  topExtra = null,
  categoryBorderClass = "border-b-2 border-slate-300",
  searchValue = "",
}) {
  // ---- Visible products (apply hidden/paused rules + search + category filter) ----
  const visibleProducts = useMemo(() => {
    const base = (products || []).filter((p) => !p.is_hidden && !p.sale_paused);
    const q = (searchValue || "").trim().toLowerCase();
    if (q) {
      // When searching, ignore category filter and match across description / code / barcodes
      return base.filter((p) => {
        const fields = [p.description, p.code, p.barcode1, p.barcode2, p.barcode3];
        return fields.some((f) => f && String(f).toLowerCase().includes(q));
      });
    }
    if (activeCat === "all") return base;
    if (activeCat === "_none") return base.filter((p) => !p.category_id);
    return base.filter((p) => p.category_id === activeCat);
  }, [products, activeCat, searchValue]);

  // ---- Favourites (first 6 sellable favourites) ----
  const favorites = useMemo(
    () => (products || []).filter((p) => p.is_favorite && !p.sale_paused).slice(0, 6),
    [products],
  );

  // ---- Tile sizing (auto-scale with grid density) ----
  const gridCols = Math.max(2, Math.min(8, settings?.products_cols || 5));
  const gridRows = Math.max(2, Math.min(8, settings?.products_rows || 4));
  const tileSizing = useMemo(() => {
    const colFactor = 5 / gridCols;
    const rowFactor = 4 / gridRows;
    const factor = Math.min(colFactor, rowFactor);
    const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
    return {
      pad: clamp(Math.round(12 * factor), 6, 18),
      descPx: clamp(Math.round(18 * factor), 12, 26),
      pricePx: clamp(Math.round(20 * factor), 14, 28),
    };
  }, [gridCols, gridRows]);

  // ---- Colour helpers ----
  const categoryColor = (id) => categories.find((c) => c.id === id)?.color || "#94A3B8";
  const productColor = (p) => p.color || categoryColor(p.category_id);

  // ---- Scroll buttons (touch fallback) ----
  const productsScrollRef = useRef(null);
  const scrollProducts = (direction) => {
    const el = productsScrollRef.current;
    if (!el) return;
    const step = Math.max(120, Math.round(el.clientHeight * 0.6));
    el.scrollBy({ top: direction * step, behavior: "smooth" });
  };

  return (
    <section data-testid="products-area" className="flex-1 h-full flex flex-col bg-slate-50 min-w-0">
      {/* Category strip */}
      <div className={`px-4 py-3 ${categoryBorderClass} bg-white`}>
        <div
          className="grid gap-2 w-full"
          style={{
            gridTemplateColumns: `repeat(${(settings?.categories_cols || 6) + 1}, minmax(0, 1fr))`,
            gridAutoRows: "64px",
            maxHeight: `${(settings?.categories_rows || 1) * 72 + 8}px`,
            overflowY: "auto",
          }}
          data-testid={`${testidPrefix}-cat-buttons`}
        >
          <button
            onClick={() => setActiveCat("all")}
            data-testid={`${testidPrefix}-cat-all`}
            className={`rounded-lg text-base font-bold border-2 transition-all active:scale-[0.97] flex items-center justify-center px-2 ${
              activeCat === "all"
                ? "bg-blue-600 text-white border-blue-700 shadow-md"
                : "bg-white border-slate-300 hover:bg-slate-50 text-slate-800"
            }`}
          >
            Όλα
          </button>
          {categories.map((c) => {
            const bg = c.color || "#2563EB";
            const fg = readableTextColor(bg);
            const isActive = activeCat === c.id;
            return (
              <button
                key={c.id}
                onClick={() => setActiveCat(c.id)}
                data-testid={`${testidPrefix}-cat-${c.id}`}
                className={`rounded-lg text-base font-bold border-2 transition-all active:scale-[0.97] flex items-center justify-center text-center px-2 leading-tight ${
                  isActive ? "shadow-md ring-2 ring-offset-2 ring-slate-800" : "hover:brightness-110"
                }`}
                style={{ background: bg, color: fg, borderColor: bg }}
              >
                {c.name}
              </button>
            );
          })}
        </div>
        {topExtra}
      </div>

      {/* Product grid + favourites footer */}
      <div className="flex-1 flex flex-col overflow-hidden">
        <div ref={productsScrollRef} className="flex-1 overflow-y-auto scroll-thin p-4">
          <div
            className="grid gap-2 w-full h-full"
            style={{
              gridTemplateColumns: `repeat(${gridCols}, minmax(0, 1fr))`,
              gridAutoRows: `minmax(${Math.max(48, Math.round(140 - gridCols * 8))}px, calc((100vh - 360px) / ${gridRows}))`,
            }}
            data-testid={`${testidPrefix}-product-grid`}
          >
            {visibleProducts.map((p) => {
              const bg = productColor(p);
              const fg = readableTextColor(bg);
              const sb = settings?.modules_stock === false ? null : stockBadge(p, settings?.stock_low_threshold ?? 5);
              return (
                <button
                  key={p.id}
                  onClick={() => onProductClick(p)}
                  data-testid={`${testidPrefix}-product-${p.id}`}
                  className="relative rounded-lg border-2 shadow-sm hover:shadow-lg hover:brightness-110 active:scale-[0.97] transition-all overflow-hidden"
                  style={{
                    background: bg,
                    color: fg,
                    borderColor: bg,
                    padding: `${tileSizing.pad}px`,
                  }}
                >
                  <div
                    className="absolute inset-0 flex items-center justify-center text-center px-2"
                    style={{ paddingBottom: `calc(3mm + ${tileSizing.pricePx + 14}px)` }}
                  >
                    <div
                      className="font-bold leading-tight line-clamp-3 w-full"
                      style={{ fontSize: `${tileSizing.descPx}px` }}
                    >
                      {p.description}
                    </div>
                  </div>
                  <div
                    className="absolute left-1/2 -translate-x-1/2 font-mono font-semibold tabular px-2 py-1 rounded-md whitespace-nowrap shadow-sm"
                    style={{
                      bottom: "3mm",
                      fontSize: `${tileSizing.pricePx}px`,
                      background: fg === "#ffffff" ? "rgba(0,0,0,0.30)" : "rgba(255,255,255,0.65)",
                    }}
                  >
                    {fmtEUR(p.price)}
                  </div>
                  {sb && (
                    <div
                      className={`absolute bottom-1 right-1 min-w-[26px] px-1.5 py-0.5 rounded-md text-[11px] font-mono font-bold text-center shadow ${sb.cls}`}
                      data-testid={`stock-badge-${p.id}`}
                    >
                      {sb.qty}
                    </div>
                  )}
                </button>
              );
            })}
            {visibleProducts.length === 0 && (
              <div className="col-span-full text-center text-slate-400 py-12">
                {(products || []).length === 0
                  ? "Δεν υπάρχουν είδη."
                  : "Καμία αντιστοίχιση για την κατηγορία."}
              </div>
            )}
          </div>
        </div>

        {/* Pinned favourites row + scroll buttons */}
        <div
          className="shrink-0 border-t-2 border-amber-200 bg-amber-50/40 px-3 py-1.5 flex items-center gap-2"
          data-testid={`${testidPrefix}-favorites-row`}
        >
          <div className="flex-1 grid grid-cols-6 gap-1.5">
            {Array.from({ length: 6 }).map((_, i) => {
              const p = favorites[i];
              if (!p) {
                return (
                  <div
                    key={`${testidPrefix}-fav-empty-${i}`}
                    className="h-14 rounded-md border-2 border-dashed border-amber-200 bg-white/40 grid place-items-center text-amber-400"
                  >
                    <Star size={14} />
                  </div>
                );
              }
              const bg = productColor(p);
              const fg = readableTextColor(bg);
              const favSb = settings?.modules_stock === false ? null : stockBadge(p, settings?.stock_low_threshold ?? 5);
              return (
                <button
                  key={p.id}
                  onClick={() => onProductClick(p)}
                  data-testid={`${testidPrefix}-fav-product-${p.id}`}
                  className="h-14 rounded-md border-2 shadow-sm hover:shadow-md hover:brightness-110 active:scale-[0.97] transition-all p-1 flex flex-col items-center justify-center text-center relative overflow-hidden"
                  style={{ background: bg, color: fg, borderColor: bg }}
                >
                  {favSb && (
                    <div className={`absolute bottom-0.5 right-0.5 min-w-[18px] px-1 rounded text-[9px] font-mono font-bold text-center ${favSb.cls}`}>
                      {favSb.qty}
                    </div>
                  )}
                  <Star
                    size={10}
                    fill="currentColor"
                    className="absolute top-0.5 right-0.5 opacity-80"
                  />
                  <div className="text-[11px] font-bold leading-tight line-clamp-2">
                    {p.description}
                  </div>
                  <div
                    className="text-xs font-mono font-semibold tabular mt-0.5 px-1 py-px rounded"
                    style={{
                      background: fg === "#ffffff" ? "rgba(0,0,0,0.25)" : "rgba(255,255,255,0.55)",
                    }}
                  >
                    {fmtEUR(p.price)}
                  </div>
                </button>
              );
            })}
          </div>
          <div className="shrink-0 flex flex-row gap-1.5">
            <button
              type="button"
              onClick={() => scrollProducts(-1)}
              className="w-14 h-14 rounded-md bg-slate-800 hover:bg-slate-900 text-white grid place-items-center shadow-md active:scale-95 transition-all"
              data-testid={`${testidPrefix}-products-scroll-up`}
              title="Πάνω"
            >
              <ChevronUp size={32} strokeWidth={3} />
            </button>
            <button
              type="button"
              onClick={() => scrollProducts(1)}
              className="w-14 h-14 rounded-md bg-slate-800 hover:bg-slate-900 text-white grid place-items-center shadow-md active:scale-95 transition-all"
              data-testid={`${testidPrefix}-products-scroll-down`}
              title="Κάτω"
            >
              <ChevronDown size={32} strokeWidth={3} />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

export { readableTextColor };
